/**
 * Tuning constants — spec §1.6.
 *
 * This is the ONLY place these numbers exist on the JS/TS side. The Swift
 * engine has a mirror (`LongshotConstants.swift`) and the two are held together
 * by the shared fixtures in `longshot/fixtures`. Never inline these values.
 */
export const CONSTANTS = {
  /** Board edge length. */
  GRID: 5,
  /** Shots per game. Firing always costs one, hit or miss. */
  BULLETS: 5,
  /**
   * Shortest fireable word, in letters ("Qu" counts as 2).
   *
   * Three, not four. At four, an ordinary player who spots CAT or SUN has
   * nothing to fire, and the boards' scoring lines were dominated by words like
   * COSTARD and ADNATION. Three-letter words are cheap heat-layers, which is
   * exactly the on-ramp the accumulate-and-square model wants: lay a short word
   * down early, then run a long one back over it.
   */
  MIN_LEN: 3,
  /** Longest word the validator and solver will consider. */
  MAX_LEN: 10,
  /** Ladder ceiling. */
  MULT_MAX: 5,
  /**
   * Maximum heat a single tile can accumulate.
   *
   * With BULLETS = 5 a tile can be crossed by at most 4 prior valid words, so
   * this never actually binds - it exists so the formula stays well defined if
   * the bullet count ever changes. Heat is deliberately uncapped in practice:
   * the whole point of the scoring model is that reusing your own tracks
   * compounds.
   */
  HEAT_CAP: 4,
  /** Value a tile gains for each prior valid word that crossed it. */
  HEAT_STEP: 1,
  /** Multiplier applied when a path crosses the gold tile. */
  GOLD_MULT: 2,
} as const;

export type Constants = typeof CONSTANTS;

/** Tile count on a board. Derived, never hardcoded as 25. */
export const TILE_COUNT = CONSTANTS.GRID * CONSTANTS.GRID;

/**
 * The subset of constants embedded in each puzzle JSON (§2). A client that
 * disagrees with these has a stale build and must refuse the puzzle rather than
 * score it differently from everyone else.
 */
export function constantsManifest() {
  return {
    minLen: CONSTANTS.MIN_LEN,
    heatCap: CONSTANTS.HEAT_CAP,
    heatStep: CONSTANTS.HEAT_STEP,
    goldMult: CONSTANTS.GOLD_MULT,
    bullets: CONSTANTS.BULLETS,
    multMax: CONSTANTS.MULT_MAX,
  };
}

/** Dictionary build this engine expects; puzzles are only valid against it (§5.1). */
export const DICT_VERSION = "dict_v1";

/** Puzzle JSON schema version (§2). */
/**
 * Puzzle JSON schema (§2).
 *
 * 2 — accumulate-and-square scoring; `heatPts` became `heatStep`.
 * 3 — MIN_LEN 4 -> 3, and the analytics block gained the "common word" fields
 *     that back the familiarity gates.
 * 4 — analytics carries `rankThresholds`, the per-board bronze..expert ladder.
 *     Derived from anchors only the generator has, so it must be shipped. Both change the checksum payload, so an
 *     older puzzle cannot be scored by this build and `validate()` refuses it
 *     rather than playing it leniently.
 */
export const SCHEMA_VERSION = 4;
